import { ReactNode, useMemo, useState } from 'react';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  // Optional override for what the search box matches against. Use when the
  // displayed cell (via `render`) differs from the raw `key` value so search
  // matches what the user actually sees.
  searchAccessor?: (row: T) => string | number | null | undefined;
}

interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  keyField?: keyof T;
  emptyMessage?: string;
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  // Opt-in checkbox column. Selection is owned by the caller so bulk actions can
  // act on it; the header checkbox covers the currently *visible* rows, so
  // "select all" while a search is active selects only the matches.
  selectable?: boolean;
  selectedKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
}

function getValue(row: any, key: string) {
  return key.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), row);
}

export default function Table<T extends Record<string, any>>({
  columns,
  rows,
  keyField = 'id' as keyof T,
  emptyMessage = 'No records found',
  loading = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  selectable = false,
  selectedKeys = [],
  onSelectionChange,
}: TableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [query, setQuery] = useState('');

  const filteredRows = useMemo(() => {
    if (!searchable || !query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((row) =>
      columns.some((col) => {
        const val = col.searchAccessor ? col.searchAccessor(row) : getValue(row, col.key);
        return String(val ?? '').toLowerCase().includes(q);
      })
    );
  }, [rows, columns, searchable, query]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const av = getValue(a, sortKey);
      const bv = getValue(b, sortKey);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredRows, sortKey, sortDir]);

  const visibleKeys = sortedRows.map((row) => String(row[keyField]));
  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selectedKeys.includes(k));

  const toggleRow = (key: string) => {
    if (!onSelectionChange) return;
    onSelectionChange(
      selectedKeys.includes(key) ? selectedKeys.filter((k) => k !== key) : [...selectedKeys, key]
    );
  };

  // Toggling the header only adds/removes the visible rows, leaving any
  // selection made under a different search term intact.
  const toggleAllVisible = () => {
    if (!onSelectionChange) return;
    onSelectionChange(
      allVisibleSelected
        ? selectedKeys.filter((k) => !visibleKeys.includes(k))
        : Array.from(new Set([...selectedKeys, ...visibleKeys]))
    );
  };

  const toggleSort = (col: Column<T>) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {searchable && (
        <div className="p-3 border-b">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full">
        <thead className="bg-gray-100 border-b">
          <tr>
            {selectable && (
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Select all rows"
                  title="Select all rows"
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={() => toggleSort(col)}
                className={`px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                  col.sortable ? 'cursor-pointer select-none hover:text-gray-900' : ''
                }`}
              >
                {col.header}
                {sortKey === col.key && (sortDir === 'asc' ? ' ▲' : ' ▼')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading && (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-6 text-center text-gray-500">
                Loading...
              </td>
            </tr>
          )}
          {!loading && sortedRows.length === 0 && (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-6 text-center text-gray-500">
                {searchable && query.trim() ? 'No matching records' : emptyMessage}
              </td>
            </tr>
          )}
          {!loading &&
            sortedRows.map((row) => (
              <tr key={String(row[keyField])} className="hover:bg-gray-50">
                {selectable && (
                  <td className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(String(row[keyField]))}
                      onChange={() => toggleRow(String(row[keyField]))}
                      aria-label="Select row"
                    />
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-3 text-sm text-gray-700">
                    {col.render ? col.render(row) : String(getValue(row, col.key) ?? '')}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
